import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';

/**
 * Compact tooltip for the Rush (static_only) campaign.
 */
const RushPopupContent = ({ pt, routeColorsData, teams }) => {
  const isMatchHQ = pt.type === 'Match HQ';
  const isWatchParty = pt.type === 'Watch Party';
  const isHiddenGem = pt.type === 'Hidden Gem';
  const showBadge = isMatchHQ || isWatchParty || isHiddenGem;
  const badgeLabel = isMatchHQ ? 'MATCH HQ' : isWatchParty ? 'WATCH PARTY' : 'GEM';
  const badgeBg = isMatchHQ ? '#da9828' : isWatchParty ? '#2a6496' : '#b3a637';

  const directionsUrl = pt.address
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent((pt.title || '') + ' ' + pt.address)}&travelmode=transit`
    : (pt.lat && pt.lng)
      ? `https://www.google.com/maps/dir/?api=1&destination=${pt.lat},${pt.lng}&travelmode=transit`
      : null;

  const hasPromo = typeof pt.description === 'string' && pt.description.trim().length > 0;
  const promoIsHtml = hasPromo && pt.description.includes('<');

  const routes = Array.isArray(pt.routes) ? pt.routes : [];
  const colorLookup = routeColorsData || {};

  return (
    <div style={{ minWidth: '180px', maxWidth: '240px' }}>
      {/* Name + badge */}
      <div style={{ marginBottom: '4px' }}>
        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#41392f' }}>
          {pt.title}
        </span>
        {showBadge && (
        <span style={{
          display: 'inline-block',
          marginLeft: '6px',
          fontSize: '0.6rem',
          fontWeight: 700,
          padding: '0.15rem 0.4rem',
          borderRadius: '3px',
          backgroundColor: badgeBg,
          color: isWatchParty ? '#fff' : '#000',
          verticalAlign: 'middle',
          lineHeight: 1.2
        }}>
          {badgeLabel}
        </span>
        )}
      </div>

      {/* Address */}
      {pt.address && (
        <div style={{ fontSize: '0.75rem', color: '#41392f', marginBottom: '4px' }}>
          <span style={{ fontWeight: 600 }}>{pt.address.replace(/,?\s*(San Francisco|SF),?\s*(CA)?\s*\d*/i, '').trim()}</span>
        </div>
      )}

      {/* Muni Directions */}
      {directionsUrl && (
        <div style={{ fontSize: '0.75rem', marginBottom: '6px' }}>
          <a
            href={directionsUrl}
            target="_blank"
            rel="noreferrer"
            style={{
              color: '#2a6496',
              fontWeight: 500,
              textDecoration: 'none'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
            onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
          >
            Muni Directions 🚌
          </a>
        </div>
      )}

      {/* Phone */}
      {pt.phone && (
        <div style={{ fontSize: '0.75rem', color: '#41392f', marginBottom: '6px' }}>
          <span style={{ fontWeight: 600 }}>Phone: </span>
          <a
            href={`tel:${pt.phone.replace(/[^+\d]/g, '')}`}
            style={{ color: '#2a6496', textDecoration: 'none', fontWeight: 500 }}
            onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
            onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
          >
            {pt.phone.replace(/^\((\d{3})\)\s*/, '$1-')}
          </a>
        </div>
      )}

      {/* Details link for Viewing License */}
      {pt.type === 'Viewing License' && pt.website_url && (
        <div style={{ fontSize: '0.75rem', color: '#41392f', marginBottom: '6px' }}>
          <span style={{ fontWeight: 600 }}>Details: </span>
          <a
            href={pt.website_url}
            target="_blank"
            rel="noreferrer"
            aria-label={`Visit the ${pt.title} website for more information.`}
            style={{ color: '#2a6496', textDecoration: 'none', fontWeight: 500 }}
            onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
            onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
          >
            Visit website
          </a>
        </div>
      )}

      {/* Nearby Routes */}
      {routes.length > 0 && (
        <div style={{ fontSize: '0.75rem', color: '#41392f', marginBottom: '6px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px' }}>
          <span style={{ fontWeight: 600 }}>Nearby Muni Routes:</span>
            {routes.map((routeId) => {
              const routeInfo = colorLookup[routeId];
              const bgColor = routeInfo ? `#${routeInfo.route_color}` : '#005B95';
              const textColor = routeInfo ? `#${routeInfo.route_text_color}` : '#FFFFFF';
              return (
                <span
                  key={routeId}
                  style={{
                    display: 'inline-block',
                    padding: '1px 5px',
                    borderRadius: '9px',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    backgroundColor: bgColor,
                    color: textColor,
                    lineHeight: 1.4
                  }}
                >
                  {routeId}
                </span>
              );
            })}
        </div>
      )}

      {/* Promotions */}
      {hasPromo && (
        promoIsHtml ? (
          <div
            style={{
              fontSize: '0.75rem',
              color: '#41392f'
            }}
            dangerouslySetInnerHTML={{ __html: pt.description }}
          />
        ) : (
          <div style={{
            fontSize: '0.75rem',
            color: '#41392f'
          }}>
            {pt.description}
          </div>
        )
      )}

      {/* National Affinities (Supports) */}
      {Array.isArray(pt.national_affinities) && pt.national_affinities.length > 0 && teams && (
        <div style={{ marginTop: '8px' }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#90795b', display: 'block', marginBottom: '3px' }}>
            Supports:
          </span>
          {pt.national_affinities.map(affinityId => {
            const team = teams.find(t => t.id === affinityId);
            if (!team) return null;
            return (
              <div key={affinityId} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                {team.flag_url && (
                  <img src={team.flag_url} alt={team.name} style={{ width: '20px', height: 'auto', borderRadius: '2px' }} />
                )}
                <span style={{ fontSize: '0.75rem', color: '#41392f' }}>{team.name}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Bar call-ahead reminder */}
      {pt.type === 'Bar' && (
        <div style={{ fontSize: '0.75rem', color: '#41392f', marginTop: '8px', fontStyle: 'italic' }}>
          {pt.google_maps_url ? (
            <>
              <a
                href={pt.google_maps_url}
                target="_blank"
                rel="noreferrer"
                style={{ color: '#2a6496', textDecoration: 'none', fontWeight: 500 }}
                onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
              >
                Check business hours
              </a>
              {' and call ahead to make sure your match will be played.'}
            </>
          ) : (
            'Check business hours and call ahead to make sure your match will be played.'
          )}
        </div>
      )}
    </div>
  );
};

const StaticContentLayer = ({ points, campaignName, pane, markerRefMapRef, isRoutesOnlyMode, campaignMode, routeColorsData, clusterGroupRef, teams }) => {
  const emojiIconCacheRef = useRef(new Map());
  const map = useMap();
  const [zoomLevel, setZoomLevel] = useState(map.getZoom());

  useEffect(() => {
    const onZoom = () => setZoomLevel(map.getZoom());
    map.on('zoomend', onZoom);
    return () => { map.off('zoomend', onZoom); };
  }, [map]);

  // Show labels next to pins when zoomed in enough (static_only mode only)
  const showLabels = campaignMode === 'static_only' && zoomLevel >= 16;
  const LABEL_MAX_CHARS = 18;

  const SOCIAL_ICON_MAP = {
    facebook: 'fa-brands fa-facebook-f',
    instagram: 'fa-brands fa-instagram',
    twitter: 'fa-brands fa-x-twitter',
    x: 'fa-brands fa-x-twitter',
    tiktok: 'fa-brands fa-tiktok',
    youtube: 'fa-brands fa-youtube',
    linkedin: 'fa-brands fa-linkedin-in'
  };

  const defaultIcon = useMemo(() => {
    const iconUrl = `${process.env.PUBLIC_URL || ''}/campaigns/${campaignName}/images/default-pin.svg`;
    return L.icon({
      iconUrl,
      iconRetinaUrl: iconUrl,
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
      iconSize: [32, 32],
      iconAnchor: [16, 16],
      popupAnchor: [0, -16],
      shadowSize: [41, 41],
      shadowAnchor: [12, 41]
    });
  }, [campaignName]);

  const getEmojiIcon = (emoji) => {
    if (typeof emoji !== 'string' || emoji.trim().length === 0) return null;
    const key = emoji.trim();
    const cached = emojiIconCacheRef.current.get(key);
    if (cached) return cached;

    const html = `
      <div style="width: 32px; height: 32px; border-radius: 16px; background: white; display: flex; align-items: center; justify-content: center; box-shadow: 0 1px 4px rgba(0,0,0,0.35); font-size: 18px; line-height: 1;">
        ${key}
      </div>
    `;

    const icon = L.divIcon({
      html,
      className: '',
      iconSize: [32, 32],
      iconAnchor: [16, 16],
      popupAnchor: [0, -16]
    });

    emojiIconCacheRef.current.set(key, icon);
    return icon;
  };

  const greyedIconCacheRef = useRef(new Map());

  const getGreyedIcon = (emoji) => {
    const key = (typeof emoji === 'string' && emoji.trim().length > 0) ? emoji.trim() : '_default';
    const cached = greyedIconCacheRef.current.get(key);
    if (cached) return cached;

    const content = key === '_default' ? '•' : key;
    const html = `
      <div style="width: 32px; height: 32px; border-radius: 16px; background: #e0e0e0; display: flex; align-items: center; justify-content: center; box-shadow: 0 1px 2px rgba(0,0,0,0.15); font-size: 18px; line-height: 1; filter: grayscale(1);">
        ${content}
      </div>
    `;

    const icon = L.divIcon({
      html,
      className: '',
      iconSize: [32, 32],
      iconAnchor: [16, 16],
      popupAnchor: [0, -16]
    });

    greyedIconCacheRef.current.set(key, icon);
    return icon;
  };

  const safePoints = Array.isArray(points) ? points : [];

  const getPointKey = (pt, lat, lng) => {
    if (pt && pt.id != null && String(pt.id).trim().length > 0) return String(pt.id);
    return `${lat},${lng}`;
  };

  const markers = safePoints.map((pt) => {
        const lat = Number(pt.lat);
        const lng = Number(pt.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

        const isGreyed = Boolean(pt.greyed);

        // Build the icon - with label if zoomed in enough and not greyed
        let iconToUse;
        if (!isGreyed && showLabels && pt.title) {
          const label = pt.title.length > LABEL_MAX_CHARS
            ? pt.title.slice(0, LABEL_MAX_CHARS) + '…'
            : pt.title;
          const emoji = (typeof pt['icon-emoji'] === 'string' && pt['icon-emoji'].trim()) || '';
          const html = `
            <div style="display: flex; flex-direction: column; align-items: center; gap: 2px; white-space: nowrap;">
              <div style="width: 32px; height: 32px; border-radius: 16px; background: white; display: flex; align-items: center; justify-content: center; box-shadow: 0 1px 4px rgba(0,0,0,0.35); font-size: 18px; line-height: 1; flex-shrink: 0;">
                ${emoji}
              </div>
              <span style="font-size: 10px; font-weight: 600; color: #41392f; background: rgba(255,255,255,0.92); padding: 1px 4px; border-radius: 3px; box-shadow: 0 1px 2px rgba(0,0,0,0.12); max-width: 100px; overflow: hidden; text-overflow: ellipsis; position: relative; z-index: 9999; cursor: pointer;">${label}</span>
            </div>
          `;
          iconToUse = L.divIcon({
            html,
            className: '',
            iconSize: [32, 48],
            iconAnchor: [16, 16],
            popupAnchor: [0, -16]
          });
        } else {
          const emojiIcon = isGreyed
            ? getGreyedIcon(pt['icon-emoji'])
            : getEmojiIcon(pt['icon-emoji']);
          iconToUse = emojiIcon || defaultIcon;
        }

        const pointKey = getPointKey(pt, lat, lng);

        return (
          <Marker
            key={pointKey}
            position={[lat, lng]}
            icon={iconToUse}
            pane={pane}
            opacity={isGreyed ? 0.4 : 1}
            zIndexOffset={isGreyed ? -1000 : (pt.type === 'Match HQ' ? 2000 : pt.type === 'Watch Party' ? 1500 : 1000)}
            ref={(markerInstance) => {
              if (!markerRefMapRef || !markerRefMapRef.current) return;
              if (markerInstance) {
                markerRefMapRef.current.set(pointKey, markerInstance);
              } else {
                markerRefMapRef.current.delete(pointKey);
              }
            }}
          >
            <Popup
              pane="popupPane"
              {...(isRoutesOnlyMode ? { autoPan: false, keepInView: false } : {})}
            >
              {campaignMode === 'static_only' ? (
                <RushPopupContent pt={pt} routeColorsData={routeColorsData} teams={teams} />
              ) : (
              <div style={{ minWidth: '200px' }}>
                {pt.title && (
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>{pt.title}</h4>
                )}
                {pt.address && (
                  <div style={{ marginBottom: '8px', fontSize: '12px', color: '#333' }}>
                    {pt.goole_maps_link ? (
                      <>
                        <strong>Address:</strong>&nbsp;
                        <strong>
                          <a
                            href={pt.goole_maps_link}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: '#007bff', textDecoration: 'none' }}
                          >
                            {pt.address}
                          </a>
                        </strong>
                      </>
                    ) : (
                      pt.address
                    )}
                  </div>
                )}
                {pt.description && (
                  <div
                    style={{ fontSize: '12px', color: '#333' }}
                    dangerouslySetInnerHTML={{ __html: pt.description }}
                  />
                )}
                {pt.website && (
                  <div style={{ marginTop: '8px', fontSize: '12px' }}>
                    <a
                      href={pt.website}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: '#007bff', textDecoration: 'none' }}
                    >
                      Visit Website
                    </a>
                  </div>
                )}
                {pt.social_media && typeof pt.social_media === 'object' && (
                  <div style={{ marginTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {Object.entries(pt.social_media)
                      .filter(([network, url]) => {
                        if (typeof url !== 'string') return false;
                        if (url.trim().length === 0) return false;
                        const key = String(network || '').toLowerCase();
                        return Boolean(SOCIAL_ICON_MAP[key]);
                      })
                      .map(([network, url]) => {
                        const key = String(network || '').toLowerCase();
                        const label = key.charAt(0).toUpperCase() + key.slice(1);
                        const iconClassName = SOCIAL_ICON_MAP[key];

                        return (
                          <a
                            key={key}
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={label}
                            title={label}
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '14px',
                              border: '1px solid #e5e7eb',
                              background: 'white',
                              color: '#0d6efd',
                              textDecoration: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              fontWeight: 800,
                              lineHeight: 1
                            }}
                          >
                            <i className={iconClassName} aria-hidden="true" />
                          </a>
                        );
                      })}
                  </div>
                )}
              </div>
              )}
            </Popup>
          </Marker>
        );
      });

  if (campaignMode === 'static_only') {
    const activeMarkers = [];
    const greyedMarkers = [];
    markers.forEach((m, i) => {
      if (!m) return;
      const pt = safePoints[i];
      if (pt && pt.greyed) {
        greyedMarkers.push(m);
      } else {
        activeMarkers.push(m);
      }
    });

    return (
      <>
        {greyedMarkers}
        <MarkerClusterGroup
          ref={clusterGroupRef}
          chunkedLoading
          maxClusterRadius={40}
          spiderfyOnMaxZoom={true}
          showCoverageOnHover={false}
          iconCreateFunction={(cluster) => {
            const count = cluster.getChildCount();
            return L.divIcon({
              html: `<div style="width:36px;height:36px;border-radius:50%;background:#da9828;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:13px;box-shadow:0 2px 6px rgba(0,0,0,0.3);">${count}</div>`,
              className: '',
              iconSize: [36, 36],
              iconAnchor: [18, 18]
            });
          }}
        >
          {activeMarkers}
        </MarkerClusterGroup>
      </>
    );
  }

  return (
    <>
      {markers}
    </>
  );
};

export default StaticContentLayer;
